import type { ReactElement } from 'react';
import { assetToast } from '@xdocs/i18n';
import { Button, Toggle, cn, useI18n, useToast } from '@xdocs/ui';
import type { NotificationPrefType } from './useNotificationPrefs.js';
import { NOTIFICATION_TYPES, useNotificationPrefs } from './useNotificationPrefs.js';
import { useSettingsT } from './useSettingsT.js';
import styles from './NotificationsPrefsSection.module.css';

/** F10 Notifications-prefs section — per-type × per-channel toggle grid. */
export function NotificationsPrefsSection(): ReactElement {
  const { locale } = useI18n();
  const { t } = useSettingsT();
  const toast = useToast();
  const { prefs, setChannel, save, isSaving, saveError } = useNotificationPrefs();

  async function handleSave(): Promise<void> {
    try {
      await save();
      toast.push(assetToast(locale, 'success', t('settings:notifications.toast.saved.action'), t('settings:notifications.title')));
    } catch {
      toast.push({ tone: 'error', message: t('settings:notifications.toast.saveFailed') });
    }
  }

  return (
    <section className={cn(styles['card'])}>
      <h2 className={cn(styles['title'])}>{t('settings:notifications.title')}</h2>
      <p className={cn(styles['description'])}>{t('settings:notifications.description')}</p>
      {import.meta.env.DEV && <p className={cn(styles['devNotice'])}>{t('settings:notifications.devNotice')}</p>}

      <div className={cn(styles['grid'])} role="table" aria-label={t('settings:notifications.title')}>
        <div className={cn(styles['gridHeader'])} role="row">
          <span role="columnheader" className={cn(styles['gridHeaderType'])} />
          <span role="columnheader">{t('settings:notifications.column.inApp')}</span>
          <span role="columnheader">{t('settings:notifications.column.webPush')}</span>
          <span role="columnheader">{t('settings:notifications.column.email')}</span>
        </div>
        {NOTIFICATION_TYPES.map((type) => (
          <NotificationPrefRow key={type} type={type} prefs={prefs} onChange={setChannel} />
        ))}
      </div>

      {saveError && (
        <p role="alert" className={cn(styles['error'])}>
          {t('settings:notifications.toast.saveFailed')}
        </p>
      )}

      <div className={cn(styles['actions'])}>
        <Button variant="primary" state={isSaving ? 'loading' : 'idle'} onClick={() => void handleSave()}>
          {t('settings:notifications.action.save')}
        </Button>
      </div>
    </section>
  );
}

interface NotificationPrefRowProps {
  type: NotificationPrefType;
  prefs: ReturnType<typeof useNotificationPrefs>['prefs'];
  onChange: ReturnType<typeof useNotificationPrefs>['setChannel'];
}

function NotificationPrefRow({ type, prefs, onChange }: NotificationPrefRowProps): ReactElement {
  const { t } = useSettingsT();
  const pref = prefs.find((p) => p.type === type);

  return (
    <div className={cn(styles['gridRow'])} role="row">
      <span role="rowheader" className={cn(styles['gridHeaderType'])}>
        {t(`settings:notifications.type.${type}`)}
      </span>
      <span role="cell">
        <Toggle
          label={t('settings:notifications.column.inApp')}
          hideLabel
          checked={pref?.inApp ?? false}
          onChange={(event) => onChange(type, 'inApp', event.target.checked)}
        />
      </span>
      <span role="cell">
        <Toggle
          label={t('settings:notifications.column.webPush')}
          hideLabel
          checked={pref?.webPush ?? false}
          onChange={(event) => onChange(type, 'webPush', event.target.checked)}
        />
      </span>
      <span role="cell">
        <Toggle
          label={t('settings:notifications.column.email')}
          hideLabel
          checked={pref?.email ?? false}
          onChange={(event) => onChange(type, 'email', event.target.checked)}
        />
      </span>
    </div>
  );
}
